import React, { useEffect, useState } from "react";

// Compacte deadline-editor voor de detail-page header. Toont een readable
// "over 5 dagen" / "3 dagen verlopen" pill in display-mode, en een datum-
// input + note-veld in edit-mode. Server stuurt reminder-mails op 7d/4d/0d/
// elke 7 dagen overdue.

const toIsoDate = (d) => {
  if (!d) return "";
  try {
    const date = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(date.getTime())) return "";
    // YYYY-MM-DD voor <input type="date">. We willen geen UTC-shift dus
    // gebruiken we de lokale datum-componenten.
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return "";
  }
};

const daysUntil = (dateLike) => {
  if (!dateLike) return null;
  const target = new Date(dateLike);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  // Verschil in dagen op basis van UTC-midden om DST-randgevallen te
  // vermijden. We rekenen alleen op datum-precisie, niet uur.
  const targetStart = Date.UTC(
    target.getFullYear(),
    target.getMonth(),
    target.getDate(),
  );
  const todayStart = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  return Math.round((targetStart - todayStart) / (1000 * 60 * 60 * 24));
};

const formatRelative = (days) => {
  if (days == null) return null;
  if (days === 0) return "vandaag";
  if (days === 1) return "morgen";
  if (days === -1) return "gisteren verlopen";
  if (days > 0) return `over ${days} dagen`;
  return `${-days} dagen verlopen`;
};

const toneFor = (days) => {
  if (days == null) return "bg-[#091E420F] text-ink-subtle";
  if (days < 0) return "bg-[#FFECEB] text-[#AE2A19]";
  if (days <= 3) return "bg-[#FFF7D6] text-[#7F5F01]";
  if (days <= 7) return "bg-brand-subtle text-brand";
  return "bg-[#DCFFF1] text-[#1F845A]";
};

const REMINDER_OPTIONS = [
  { value: "7d", label: "7 dagen ervoor" },
  { value: "4d", label: "4 dagen ervoor" },
  { value: "0d", label: "Op de dag zelf" },
  { value: "overdue", label: "Wekelijks bij overdue" },
];
const DEFAULT_SCHEDULE = ["7d", "4d", "0d", "overdue"];

const BtwDeadlinePicker = ({
  value,
  note,
  schedule,
  onSave,
  disabled = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [dateInput, setDateInput] = useState("");
  const [noteInput, setNoteInput] = useState("");
  const [scheduleInput, setScheduleInput] = useState(DEFAULT_SCHEDULE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!editing) {
      setDateInput(toIsoDate(value));
      setNoteInput(note ?? "");
      setScheduleInput(
        Array.isArray(schedule) && schedule.length > 0
          ? schedule
          : DEFAULT_SCHEDULE,
      );
      setError(null);
    }
  }, [editing, value, note, schedule]);

  const toggleReminder = (key) => {
    setScheduleInput((prev) =>
      prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key],
    );
  };

  const days = daysUntil(value);
  const relative = formatRelative(days);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Lege datum = clearen. Anders ISO-datum (00:00 lokaal -> server slaat
      // 'm op als UTC en de cron rekent met NL-tijd).
      const payload = dateInput
        ? {
            deadlineAt: new Date(`${dateInput}T08:00:00`).toISOString(),
            deadlineNote: noteInput.trim(),
            reminderSchedule: scheduleInput,
          }
        : { deadlineAt: null, deadlineNote: "", reminderSchedule: [] };
      await onSave?.(payload);
      setEditing(false);
    } catch (e) {
      setError(
        e?.response?.data?.error ?? e?.message ?? "Opslaan mislukt.",
      );
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave?.({ deadlineAt: null, deadlineNote: "" });
      setEditing(false);
    } catch (e) {
      setError(e?.response?.data?.error ?? "Wissen mislukt.");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="space-y-2">
        <input
          type="date"
          value={dateInput}
          onChange={(e) => setDateInput(e.target.value)}
          disabled={saving || disabled}
          className="w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none disabled:opacity-50"
        />
        <input
          type="text"
          value={noteInput}
          onChange={(e) => setNoteInput(e.target.value)}
          disabled={saving || disabled}
          maxLength={500}
          placeholder="Reden (bv. uiterste reactie-datum klant)"
          className="w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink placeholder:text-ink-subtlest focus:border-brand focus:outline-none disabled:opacity-50"
        />
        {dateInput ? (
          <div className="rounded bg-sunken p-2">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
              Stuur mij een reminder
            </p>
            <div className="flex flex-col gap-1">
              {REMINDER_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className="flex cursor-pointer items-center gap-2 text-xs text-ink"
                >
                  <input
                    type="checkbox"
                    checked={scheduleInput.includes(opt.value)}
                    onChange={() => toggleReminder(opt.value)}
                    disabled={saving || disabled}
                    className="h-4 w-4 rounded border-line text-brand focus:ring-0"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
        ) : null}
        {error ? (
          <p className="text-xs text-[#AE2A19]">{error}</p>
        ) : null}
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={save}
            disabled={saving || disabled}
            className="h-8 rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Opslaan…" : "Opslaan"}
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            disabled={saving}
            className="h-8 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Annuleren
          </button>
          {value ? (
            <button
              type="button"
              onClick={clear}
              disabled={saving || disabled}
              className="ml-auto h-8 rounded px-3 text-sm font-medium text-[#AE2A19] hover:bg-[#FFECEB] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Wissen
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      disabled={disabled}
      className="inline-flex h-8 items-center gap-2 rounded px-2 text-sm hover:bg-[#091E420F] disabled:cursor-not-allowed disabled:opacity-50"
    >
      {value ? (
        <>
          <span
            className={`inline-flex items-center rounded px-1.5 py-px text-[11px] font-bold uppercase tracking-wide ${toneFor(days)}`}
          >
            {relative}
          </span>
          <span className="text-ink">
            {new Date(value).toLocaleDateString("nl-NL")}
          </span>
        </>
      ) : (
        <span className="text-ink-subtlest">Geen deadline · klik om in te stellen</span>
      )}
    </button>
  );
};

export default BtwDeadlinePicker;
